import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Loader2, ShoppingBag, Radio, Ticket, Eye, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CONFIG = {
  soko: { path: "/pillars/soko/listings", key: "listings", icon: ShoppingBag, title: "Tournament Marketplace", sub: "Tickets, merch & collectibles from NextGen tournaments" },
  broadcast: { path: "/pillars/broadcast/streams", key: "streams", icon: Radio, title: "Tournament Broadcast", sub: "Live streams & replays from the NextGen engine" },
};

const kes = (n) => `KES ${Number(n).toLocaleString()}`;

export const NextGenPillarModal = ({ open, onOpenChange, pillar, onLogin }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [phase, setPhase] = useState("loading");
  const [source, setSource] = useState("");
  const cfg = pillar ? CONFIG[pillar.id] : null;

  useEffect(() => {
    if (!open || !cfg) return;
    setPhase("loading");
    api
      .get(cfg.path)
      .then(({ data }) => {
        setItems(data[cfg.key] || []);
        setSource(data.source);
        setPhase(data.available ? "ready" : "fallback");
      })
      .catch(() => setPhase("fallback"));
  }, [open, cfg]);

  if (!pillar || !cfg) return null;
  const Icon = cfg.icon;

  const handoff = async () => {
    if (!user) {
      toast.error("Sign in with One Talanta ID to continue");
      onOpenChange(false);
      onLogin();
      return;
    }
    try {
      const { data } = await api.post("/sport/handoff", { target: pillar.id });
      toast.success(`Secure handoff created — opening ${pillar.name}`);
      window.open(data.deep_link, "_blank", "noopener");
    } catch {
      toast.error("Could not create secure handoff");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`nextgen-pillar-modal-${pillar.id}`} className="sm:max-w-lg p-0 overflow-hidden border-0 bg-white">
        <DialogTitle className="sr-only">{pillar.name}</DialogTitle>
        <DialogDescription className="sr-only">{cfg.sub}</DialogDescription>
        <div className="kenya-stripe h-1.5" />
        <div className="bg-talanta-green px-6 pt-6 pb-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-talanta-gold text-xs font-semibold uppercase tracking-widest">
                <Icon className="h-4 w-4" /> {pillar.name}
              </div>
              <h2 className="mt-1.5 font-display font-bold text-2xl">{cfg.title}</h2>
              <p className="text-sm text-white/60">{cfg.sub}</p>
            </div>
            {source && (
              <span data-testid="nextgen-source-badge" className="text-[10px] uppercase tracking-wider font-semibold rounded-full bg-white/10 px-2 py-1">
                NextGen · {source}
              </span>
            )}
          </div>
        </div>

        <div className="px-6 py-5">
          {phase === "loading" && (
            <div className="flex items-center justify-center py-12 text-talanta-green gap-2">
              <Loader2 className="h-5 w-5 animate-spin" /> Syncing with NextGen…
            </div>
          )}
          {phase === "fallback" && (
            <div className="flex flex-col items-center py-8 text-center">
              <AlertTriangle className="h-8 w-8 text-talanta-red" />
              <p className="mt-3 font-display font-semibold text-talanta-green">NextGen engine unavailable</p>
              <p className="text-sm text-talanta-charcoal/60">Try again shortly.</p>
            </div>
          )}
          {phase === "ready" && (
            <div data-testid={`nextgen-${pillar.id}-list`} className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {items.map((it) => (pillar.id === "soko" ? <ListingRow key={it.id} it={it} /> : <StreamRow key={it.id} it={it} />))}
            </div>
          )}

          <button
            data-testid="nextgen-handoff-btn"
            onClick={handoff}
            className="mt-5 w-full rounded-xl bg-talanta-red hover:bg-talanta-red/90 py-3 text-white font-semibold flex items-center justify-center gap-2 transition-all hover:scale-[1.01]"
          >
            Continue to {pillar.name} <ExternalLink className="h-4 w-4" />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ListingRow = ({ it }) => (
  <div className="flex items-center gap-3 rounded-xl border border-talanta-green/10 bg-talanta-cream p-2.5">
    <img src={it.image} alt="" className="h-14 w-14 rounded-lg object-cover shrink-0" />
    <div className="min-w-0 flex-1">
      <div className="font-semibold text-talanta-charcoal text-sm truncate">{it.title}</div>
      <div className="text-xs text-talanta-charcoal/50 flex items-center gap-1"><Ticket className="h-3 w-3" /> {it.category} · {it.seller}</div>
    </div>
    <div className="text-right">
      <div className="font-display font-bold text-talanta-green text-sm">{kes(it.price_kes)}</div>
      <div className="text-[10px] text-talanta-charcoal/40">{it.stock} left</div>
    </div>
  </div>
);

const STATUS = { live: "bg-red-500 text-white", replay: "bg-talanta-green/10 text-talanta-green", scheduled: "bg-amber-500/15 text-amber-700" };

const StreamRow = ({ it }) => (
  <div className="flex items-center gap-3 rounded-xl border border-talanta-green/10 bg-talanta-cream p-2.5">
    <div className="relative h-14 w-20 shrink-0 rounded-lg overflow-hidden">
      <img src={it.thumbnail} alt="" className="h-full w-full object-cover" />
      <span className={`absolute bottom-1 left-1 text-[9px] font-bold uppercase rounded px-1.5 py-0.5 ${STATUS[it.status]}`}>{it.status}</span>
    </div>
    <div className="min-w-0 flex-1">
      <div className="font-semibold text-talanta-charcoal text-sm truncate">{it.title}</div>
      <div className="text-xs text-talanta-charcoal/50">{it.channel} · {it.sport}</div>
    </div>
    {it.viewers > 0 && (
      <div className="text-xs text-talanta-charcoal/60 flex items-center gap-1 shrink-0"><Eye className="h-3.5 w-3.5" /> {it.viewers.toLocaleString()}</div>
    )}
  </div>
);
